import type { LucideIcon } from 'lucide-react';
import {
  Blocks,
  BrainCircuit,
  Code,
  Database,
  Palette,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Profile                                                             */
/* ------------------------------------------------------------------ */

export const profile = {
  name: 'Immaculate Munde',
  firstName: 'Immaculate',
  lastName: 'Munde',
  age: 20,
  born: 'June 9, 2006',
  location: 'Nairobi, Kenya',
  school: 'JKUAT',
  degree: 'BSc Mathematics & Computer Science',
  tagline:
    'Twenty, Nairobi-based, and allergic to boring software. I build AI products, Web3 rails and interfaces with a fashion degree\u2019s worth of taste \u2014 usually all in the same week.',
  roles: [
    'CTO @ MissFit Tech',
    'Blockchain Developer',
    'AI Engineer',
    'Mathematician',
    'Hackathon Regular',
    'Peer Mentor',
  ],
};

export const heroChips = [
  '20 · Nairobi, KE',
  'BSc Math + CS @ JKUAT',
  'Safaricom Tech Intern \u201926',
  '8+ blockchains shipped',
];

export const stats = [
  { value: '20', label: 'years old, already a CTO' },
  { value: '8+', label: 'blockchains shipped on' },
  { value: '10+', label: 'hackathons & bootcamps' },
  { value: '12', label: 'months. That\u2019s all it took.' },
];

export interface AboutCard {
  icon: string;
  title: string;
  body: string;
  accent: string;
}

export const aboutCards: AboutCard[] = [
  {
    icon: 'GraduationCap',
    title: 'The Classroom',
    body: 'BSc in Mathematics & Computer Science at JKUAT \u2014 where the proofs are hard and the deadlines are harder.',
    accent: 'text-comet',
  },
  {
    icon: 'Crown',
    title: 'The Founder',
    body: 'Co-founder & CTO of MissFit Tech, plus PR Secretary for JKU-SIAM, the Society for Industrial & Applied Mathematics.',
    accent: 'text-nova',
  },
  {
    icon: 'Rocket',
    title: 'The Next Chapter',
    body: 'Incoming Safaricom Internship Program \u2014 Technology Track, October 2026. The big leagues.',
    accent: 'text-flare',
  },
  {
    icon: 'HeartHandshake',
    title: 'The Give-Back',
    body: 'Peer mentor with Education For All Children, and behind-the-scenes crew for TechRun Nairobi.',
    accent: 'text-mint',
  },
];

/* ------------------------------------------------------------------ */
/* Technical architecture                                              */
/* ------------------------------------------------------------------ */

export interface StackCategory {
  title: string;
  blurb: string;
  icon: LucideIcon;
  accent: string;
  glow: string;
  items: string[];
  wide?: boolean;
}

export const stack: StackCategory[] = [
  {
    title: 'Frontend & Backend',
    blurb: 'Full-stack, full-send.',
    icon: Code,
    accent: 'text-nova',
    glow: 'group-hover:shadow-[0_0_60px_-12px_hsl(14_95%_60%/0.45)]',
    items: [
      'React', 'Next.js', 'TypeScript', 'JavaScript', 'Tailwind CSS',
      'Node.js', 'Express.js', 'Python', 'Flask', 'Django', 'Java', 'PHP', 'Rust',
    ],
    wide: true,
  },
  {
    title: 'Web3 & Blockchain',
    blurb: 'Nine chains and counting.',
    icon: Blocks,
    accent: 'text-pulsar',
    glow: 'group-hover:shadow-[0_0_60px_-12px_hsl(265_90%_68%/0.45)]',
    items: [
      'Smart Contracts', 'dApps', 'Payment Protocols', 'Bitcoin', 'Lightning',
      'Base', 'Avalanche', 'Celo', 'Hedera', 'Sui', 'Soroban on Stellar', 'Solana',
    ],
    wide: true,
  },
  {
    title: 'Artificial Intelligence',
    blurb: 'Models with manners.',
    icon: BrainCircuit,
    accent: 'text-comet',
    glow: 'group-hover:shadow-[0_0_60px_-12px_hsl(195_95%_62%/0.45)]',
    items: [
      'Computer Vision', 'RAG', 'Agentic Tools', 'Vercel AI SDK',
      'Claude Code CLI', 'Gemini CLI', 'Cursor', 'OpenClaw',
    ],
  },
  {
    title: 'Data & Infrastructure',
    blurb: 'Where the state lives.',
    icon: Database,
    accent: 'text-flare',
    glow: 'group-hover:shadow-[0_0_60px_-12px_hsl(38_95%_58%/0.45)]',
    items: [
      'PostgreSQL', 'MySQL', 'SQL', 'Supabase', 'Firebase', 'Prisma', 'Neon DB', 'Redis', 'R', 'RStudio',
    ],
  },
  {
    title: 'Environment & Design',
    blurb: 'Taste is a feature.',
    icon: Palette,
    accent: 'text-blush',
    glow: 'group-hover:shadow-[0_0_60px_-12px_hsl(330_90%_66%/0.45)]',
    items: ['Ubuntu Linux', 'Canva', 'Figma', 'Google Stitch', 'v0'],
  },
];

/* ------------------------------------------------------------------ */
/* Projects                                                            */
/* ------------------------------------------------------------------ */

export interface Project {
  name: string;
  role: string;
  description: string;
  tags: string[];
  accent: string;
  gradient: string;
}

export const featuredProject = {
  name: 'MissFit Tech',
  kicker: 'Flagship venture',
  tagline: 'Fashion that finally fits.',
  description:
    'Co-founded and architected the MissFit AI Sizing Widget \u2014 computer-vision sizing for fashion retail. Accepted into The Bridge Accelerator, submitted to the Kenya AI Accelerator Programme, and currently piloting with Nairobi retailer ShopZetu.',
  tags: ['AI Sizing Widget', 'Computer Vision', 'The Bridge Accelerator', 'ShopZetu'],
};

export const projects: Project[] = [
  {
    name: 'Kuzana Brain',
    role: 'AI + Avalanche',
    description:
      'An AI-powered institutional knowledge assistant with tokenized resource unlocking, deployed on the Avalanche Fuji testnet.',
    tags: ['RAG', 'Avalanche', 'Tokenization'],
    accent: 'text-pulsar',
    gradient: 'from-pulsar/25 via-transparent to-transparent',
  },
  {
    name: 'Guidemate',
    role: 'AI Agents',
    description:
      'An AI agent-vouched booking platform for local tour guides, with automated payouts doing the paperwork.',
    tags: ['Agentic AI', 'Bookings', 'Auto-payouts'],
    accent: 'text-mint',
    gradient: 'from-mint/25 via-transparent to-transparent',
  },
  {
    name: 'LastMile',
    role: 'Stellar + Offline-first',
    description:
      'Offline-tolerant delivery verification for SAPCONE \u2014 physical reference cards meet Stellar blockchain settlement.',
    tags: ['Stellar', 'Offline-first', 'SAPCONE'],
    accent: 'text-comet',
    gradient: 'from-comet/25 via-transparent to-transparent',
  },
  {
    name: 'HoldPay',
    role: 'Bitcoin Lightning',
    description:
      'An escrow platform powered by the Bitcoin Lightning Network. Co-built at the Dada Devs \u00d7 Bitnob Bootcamp.',
    tags: ['Lightning', 'Escrow', 'Bitcoin'],
    accent: 'text-flare',
    gradient: 'from-flare/25 via-transparent to-transparent',
  },
  {
    name: 'EcoEye',
    role: 'Community + Vision AI',
    description:
      'Community-driven environmental monitoring: report threats, and automated image classification does the triage.',
    tags: ['Computer Vision', 'Climate', 'Reporting'],
    accent: 'text-mint',
    gradient: 'from-mint/25 via-transparent to-transparent',
  },
  {
    name: 'ClipStash',
    role: 'Web3 Tooling',
    description:
      'A Web3 clipboard for cross-platform text sharing via session codes. Copy here, paste there, no accounts.',
    tags: ['Web3', 'Session Codes', 'Utility'],
    accent: 'text-blush',
    gradient: 'from-blush/25 via-transparent to-transparent',
  },
];

export const clientWork: Project[] = [
  {
    name: '1000 Eyes Project',
    role: 'Davis & Shirtliff · CSR',
    description:
      'A CSR platform for Davis & Shirtliff\u2019s 1000 Eyes Project \u2014 production-grade, shipped, live.',
    tags: ['CSR Platform', 'Production', 'Client'],
    accent: 'text-comet',
    gradient: 'from-comet/25 via-transparent to-transparent',
  },
  {
    name: 'Godrick Insurance',
    role: 'Next.js + Brand',
    description:
      'A Next.js website with a full suite of marketing graphics \u2014 code and Canva, end to end.',
    tags: ['Next.js', 'Marketing Design', 'Client'],
    accent: 'text-nova',
    gradient: 'from-nova/25 via-transparent to-transparent',
  },
];

/* ------------------------------------------------------------------ */
/* Journey timeline                                                    */
/* ------------------------------------------------------------------ */

export interface Milestone {
  period: string;
  title: string;
  body: string;
  highlights: string[];
  accent: string;
  dot: string;
}

export const milestones: Milestone[] = [
  {
    period: 'Oct \u2013 Dec 2025',
    title: 'Hackathons & first builds',
    body: 'The origin arc. GirlCode Hackathon, then co-developing HoldPay at the Dada Devs \u00d7 Bitnob Bootcamp, shipping EcoEye, and closing out the ALX Africa Backend capstone with TaskTide.',
    highlights: ['GirlCode Hackathon', 'HoldPay', 'EcoEye', 'ALX Capstone'],
    accent: 'text-nova',
    dot: 'bg-nova',
  },
  {
    period: 'Jan \u2013 Mar 2026',
    title: 'Web3 fellowships & tooling',
    body: 'Bitcoin Dada Cohort 10, selected as a SheFi Scholar (Season 16), deployed the ClipStash Web3 tool, and went deep on Base autonomous agents at the Adamur OpenClaw workshop.',
    highlights: ['Bitcoin Dada C10', 'SheFi Scholar S16', 'ClipStash', 'OpenClaw on Base'],
    accent: 'text-pulsar',
    dot: 'bg-pulsar',
  },
  {
    period: 'Apr \u2013 May 2026',
    title: 'Client platforms & accelerators',
    body: 'Shipped production platforms for Davis & Shirtliff and Godrick Insurance. Stellar Impact Bootcamp and Dev3Pack Global Hackathon \u2014 then MissFit Tech got into The Bridge Accelerator.',
    highlights: ['Davis & Shirtliff', 'Godrick Insurance', 'Dev3Pack', 'The Bridge Accelerator'],
    accent: 'text-comet',
    dot: 'bg-comet',
  },
  {
    period: 'Jun \u2013 Jul 2026',
    title: 'Ecosystem expansion',
    body: 'Built Kuzana Brain on Avalanche and LastMile on Stellar. Pitched at the AYSC Climate Hackathon and Stella Gift Kenya \u2014 and locked in a spot in Safaricom\u2019s October 2026 technology cohort.',
    highlights: ['Kuzana Brain', 'LastMile', 'AYSC Climate', 'Safaricom \u201926'],
    accent: 'text-flare',
    dot: 'bg-flare',
  },
  {
    period: 'Aug \u2013 Sep 2026',
    title: 'Mentorship & AI integration',
    body: 'Co-developed Guidemate, submitted the MissFit AI Sizing Widget to the Kenya AI Accelerator Programme, deployed contracts at the Team1 Kenya MiniHack, and peer-led Dada Developers Cohort 5.',
    highlights: ['Guidemate', 'Kenya AI Accelerator', 'Team1 MiniHack', 'Dada Devs Peer Lead'],
    accent: 'text-mint',
    dot: 'bg-mint',
  },
  {
    period: 'Oct 2026 \u2192',
    title: 'The Safaricom chapter',
    body: 'Technology Track, Safaricom Internship Program. Home of M-PESA, meet the girl who builds payment rails for fun.',
    highlights: ['Safaricom', 'Technology Track', 'Incoming'],
    accent: 'text-blush',
    dot: 'bg-blush',
  },
];

/* ------------------------------------------------------------------ */
/* Beyond the code                                                     */
/* ------------------------------------------------------------------ */

export interface Interest {
  icon: string;
  title: string;
  kicker: string;
  body: string;
  accent: string;
  chips: string[];
}

export const interests: Interest[] = [
  {
    icon: 'Mountain',
    title: 'Off the grid',
    kicker: 'Multi-day hiking',
    body: 'When the laptop closes, the trail opens. Multi-day hikes and outdoor recreation \u2014 the original offline-first architecture.',
    accent: 'text-mint',
    chips: ['Multi-day treks', 'Fresh air', 'No Wi-Fi required'],
  },
  {
    icon: 'Brush',
    title: 'Art & fashion',
    kicker: 'The secret ingredient',
    body: 'Art and fashion design \u2014 the reason every interface she ships has taste. Pixels are just fabric with better version control.',
    accent: 'text-blush',
    chips: ['Design systems', 'Editorial layouts', 'Color theory'],
  },
  {
    icon: 'AudioLines',
    title: 'On repeat',
    kicker: 'Build soundtrack',
    body: 'Sauti Sol for the Nairobi energy, Celine Dion for the dramatic deploy-day power ballads. Range.',
    accent: 'text-flare',
    chips: ['Sauti Sol', 'Celine Dion'],
  },
];

/* ------------------------------------------------------------------ */
/* Contact & socials                                                   */
/* ------------------------------------------------------------------ */

export const contact = {
  email: 'immaculatemunde@gmail.com',
  socials: [
    {
      label: 'GitHub',
      handle: '@immaculate-munde',
      href: 'https://github.com/immaculate-munde',
      icon: 'github',
    },
    {
      label: 'LinkedIn',
      handle: 'in/immaculate-munde',
      href: 'https://www.linkedin.com/in/immaculate-munde/',
      icon: 'linkedin',
    },
    {
      label: 'X',
      handle: '@Imm9cul9t3',
      href: 'https://x.com/Imm9cul9t3',
      icon: 'x',
    },
  ],
} as const;

export const portrait = {
  src: '/immaculate.jpg',
  alt: 'Immaculate Munde smiling, holding a tablet and stylus in a Nairobi workspace',
  caption: 'Immaculate, in her natural habitat',
};

/* ------------------------------------------------------------------ */
/* Marquee                                                             */
/* ------------------------------------------------------------------ */

export const marqueeWords = [
  'React', 'Rust', 'Solana', 'Stellar', 'Lightning', 'AI Agents', 'RAG',
  'Next.js', 'Avalanche', 'TypeScript', 'Computer Vision', 'Base',
  'PostgreSQL', 'Celo', 'Hedera', 'Sui', 'Figma', 'Python',
];
